import { Box, Button, Stack, Typography } from '@mui/material'
import styled from '@emotion/styled'
import React, { useMemo } from 'react'

import { renderChip } from 'utils/chip'
import { CustomTooltip } from '@hi/brabus'
import { useConversionReport } from 'hooks/useConversionReport'
import { compareDesc, format, parseISO } from 'date-fns'
import { ReportProblemOutlined } from '@mui/icons-material'
import { currencyFormatter, floatNumberFormatter, integerNumberFormatter } from 'utils/formatters'
import useAdsAccounts from 'queries/useAdAccounts'
import { initialState as initialStateConversionReport } from 'contexts/conversion-report/reducer'
import { ReactComponent as FolderSvg } from './folder.svg'
import { ReactComponent as HiSvg } from './hi-brand.svg'
import { ReactComponent as MetaSvg } from './meta-brand.svg'
import { ReactComponent as TabIcon } from './tab-icon.svg'

import Grid from '../Grid'
import AdsPreview from '../AdsPreview'
import Header from './Header'

const StyledTypography = styled(({ ...props }) => <Typography {...props} />)(() => ({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

function DataGridReport({ reportData, isFetching, totalRows, disableVirtualization=false }) {
  const { state, actions } = useConversionReport()

  const { search } = state
  const { data: accounts } = useAdsAccounts()
  const selectedAccount = accounts?.find((acc) => acc.adAccountId === state.adAccountId)

  const { rows, total } = useMemo(() => {
    let rowsItems = []
    let totalItem = 0

    if (reportData) {
      rowsItems = reportData
      totalItem = totalRows
    }
    return { rows: rowsItems, total: totalItem }
  }, [reportData])

  const handleClickDetails = (row) => {
    if (search === 'campaign') {
      actions.setPagination({
        current: initialStateConversionReport.page.current,
        size: initialStateConversionReport.page.size,
      })
      actions.setSearch('ad_id')
      actions.setCampaigns([row.CampaignId])
      actions.setStatuses([])
    } else {
      // window.open(row.Url)
      const advertisement = {}

      Object.assign(advertisement, {
        status: row.Status?.toLocaleLowerCase() === 'active' ?? false,
        campaignStatus: row.CampaignStatus?.toLocaleLowerCase() === 'active' ?? false,
        campaignName: row.CampaignName,
        name: row.Name,
        url: row.Url,
        headline: row.Headline,
        mediaType: row.MediaType,
        imageUrl: row.ImageUrl,
        body: row.Body,
      })
      actions.setAdsPreview(advertisement)
    }
  }

  const columns = [
    {
      field: 'advertisement',
      sortable: search === 'advertisement',
      width: 172,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Nome do anúncio
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <TabIcon fill="#9E9E9E" width={15} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Anúncio
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={row.Name}>
              <Typography variant="body1" sx={{ textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.Name}
              </Typography>
            </CustomTooltip>
            <Typography variant="caption" color="#666" fontWeight="normal">
              ID - {row.AdId}
            </Typography>
          </Stack>
        )
      },
    },
    {
      field: 'CampaignName',
      sortable: search === 'campaign',
      width: 172,
      renderHeader: () => (
        <CustomTooltip
          title={
            <Stack p={1}>
              <Typography variant="inherit" fontWeight="bold">
                Nome da campanha
              </Typography>
            </Stack>
          }
          bordercolor="#DADDE9"
          bgcolor="#F5F5F9"
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <FolderSvg fill="#9E9E9E" width={15} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Campanha
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ row }) => {
        return (
          <Stack flexDirection="column" sx={{ width: '100%' }}>
            <CustomTooltip title={row.CampaignName}>
              <Typography variant="body1" sx={{ textWrap: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.CampaignName}
              </Typography>
            </CustomTooltip>
            <Typography variant="caption" color="#666" fontWeight="normal">
              ID - {row.CampaignId}
            </Typography>
          </Stack>
        )
      },
    },
    {
      field: 'CampaignCreatedTime',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="Data de Criação"
          body="A data em que a campanha, o conjunto ou o anúncio foram criados na sua conta Meta Ads."
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Data de Criação
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {compareDesc(parseISO(selectedAccount.connectedAt), parseISO(value)) === -1 && (
            <CustomTooltip body="Campanha criada anterior à sua conexão com Meta Ads.">
              <ReportProblemOutlined color="warning" sx={{ fontSize: 18, marginRight: '4px' }} />
            </CustomTooltip>
          )}
          {format(new Date(value), 'dd/MM/yyyy')}
        </Box>
      ),
    },
    {
      field: 'CampaignStatus',
      sortable: false,
      width: 114,
      renderHeader: () => (
        <CustomTooltip title="Veiculação" body="O status atual da veiculação da sua campanha.">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Veiculação
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => {
        const content = renderChip(value)
        return (
          <Box flex={1} display="flex" align="center" justifyContent="center">
            {content}
          </Box>
        )
      },
    },
    {
      field: 'Status',
      sortable: false,
      width: 114,
      renderHeader: () => (
        <CustomTooltip title="Veiculação" body="O status atual da veiculação do anúncio na campanha.">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Veiculação
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => {
        const content = renderChip(value)
        return (
          <Box flex={1} display="flex" align="center" justifyContent="center">
            {content}
          </Box>
        )
      },
    },
    {
      field: 'Spend',
      sortable: false,
      width: 118,
      renderHeader: () => (
        <CustomTooltip
          title="Valor gasto"
          body="Valor total estimado de dinheiro que você gastou em sua campanha, conjunto de anúncios ou anúncio
                durante sua programação."
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Valor gasto
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {currencyFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Impressions',
      sortable: false,
      width: 120,
      renderHeader: () => (
        <CustomTooltip title="Impressões" body="Número de vezes que o anúncio foi impresso." placement="top">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Impressões
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {integerNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Reach',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="Alcance"
          body="Número de pessoas que viram seus anúncios pelo menos uma vez."
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Alcance
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {integerNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Clicks',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip title="Cliques" body="Número de cliques em anúncios." placement="top">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Cliques
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {integerNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Ctr',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="CTR (taxa de cliques)"
          body="A porcentagem de vezes que as pessoas viram seu anúncio e clicaram (todas)."
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              CTR
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {floatNumberFormatter.format(value)} %
        </Box>
      ),
    },
    {
      field: 'Cpc',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip title="CPC (Custo por clique)" body="O custo médio de cada clique (todos)." placement="top">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#0076F1',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <MetaSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              CPC
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {currencyFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'ClicksToMessage',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="Conversas iniciadas"
          body="Número de conversas que foram iniciadas a partir de um anúncio de Click to WhatsApp."
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Conversas iniciadas
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {integerNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'ConversionRate',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="Taxa de conversas iniciadas"
          body="A porcentagem de conversas iniciadas pelo número de cliques no anúncio."
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Taxa de conversas iniciadas
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {floatNumberFormatter.format(value)} %
        </Box>
      ),
    },
    {
      field: 'ConversionCost',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="Custo por conversão"
          body="Custo médio das conversas que foram iniciadas a partir e um anúncio de &lsquo;Click to WhatsApp&lsquo;."
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Custo por conversão
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {currencyFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Sales',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip title="Compras" body="Total de compras realizadas." placement="top">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Compras
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {integerNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'SalesAmount',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip
          title="Valor de conversão de compras"
          body="Valor total de compras realizadas a partir dessa campanha."
          placement="top"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Valor de conversão de compras
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {currencyFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Roas',
      sortable: false,
      width: 112,
      renderHeader: () => (
        <CustomTooltip title="Roas" body="Retorno sobre o investimento em publicidade." placement="top">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              ROAS
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {floatNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'Leads',
      sortable: false,
      width: 115,
      renderHeader: () => (
        <CustomTooltip title="Leads" body="Total de Cadastros / Leads." placement="top">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              ':hover': {
                svg: {
                  fill: '#00AEEF',
                },
              },
              bgcolor: 'none',
              gap: '0.25rem',
            }}
          >
            <HiSvg fill="#9E9E9E" width={20} />
            <StyledTypography variant="inherit" fontWeight="bold" whiteSpace="break-spaces" lineHeight="1.2rem">
              Cadastros/ Leads
            </StyledTypography>
          </Box>
        </CustomTooltip>
      ),
      renderCell: ({ value }) => (
        <Box flex={1} display="flex" align="center" justifyContent="center">
          {integerNumberFormatter.format(value)}
        </Box>
      ),
    },
    {
      field: 'actions',
      sortable: false,
      headerName: '',
      width: 142,
      // TODO: abrir modal de detalhes do anúncio
      renderCell: ({ row }) => (
        <Box 
          flex={1} 
          display="flex" 
          align="center" 
          justifyContent="center"
        >
          <Button
            variant="text"
            color="primary"
            data-testid={`button-details-${row.Id}`}          
            sx={{ color: '#00AEEF', fontSize: 13, display: 'flex', gap: '4px', pointerEvents: 'all !important' }}
            disabled={search === 'ad_id' && !row.Url}
            onClick={() => {
              handleClickDetails(row)
            }}
          >
            {search === 'ad_id' && !row.Url && (
              <CustomTooltip body="Anúncio oculto devido a data criação anterior à conexão e ausência de conversas iniciadas.">
                <ReportProblemOutlined color="warning" sx={{ fontSize: 18 }} />
              </CustomTooltip>
            )}
            {search === 'campaign' ? 'Ver anúncios' : 'Ver detalhes'}
          </Button>
        </Box>
      ),
    },
  ]

  return (
    <Box
      display="flex"
      mt="1rem"
      flexDirection="column"
      border={2}
      borderColor="divider"
      p="1rem"
      gap="1.5rem"
      sx={{ borderRadius: '4px' }}
    >
      <Header search={state.search} ads={state.ads} campaigns={state.campaigns} reportData={reportData} />
      <Grid
        columns={columns}
        loading={isFetching}
        rows={rows}
        getRowId={(el) => el.Id}
        rowCount={total || 0}
        pageSizeOptions={[10, 25, 50, 100]}
        disableVirtualization={disableVirtualization}
      />
      <AdsPreview />
    </Box>
  )
}

export default DataGridReport
